import React from "react";

export function StatCard({ label, value, sub, tone = "brand", testid }) {
  const tones = {
    brand:  "bg-white border-emerald-900/10",
    accent: "bg-[#F0FAF5] border-emerald-700/20",
    warm:   "bg-[#FDF6EE] border-orange-700/20",
    coral:  "bg-[#FEF3F0] border-rose-700/20",
  };
  return (
    <div data-testid={testid} className={`rounded-xl border ${tones[tone]} p-5 transition-shadow hover:shadow-sm`}>
      <div className="overline">{label}</div>
      <div className="mt-2 font-display text-3xl font-extrabold text-emerald-900 tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function Section({ title, kicker, right, children, className = "" }) {
  return (
    <section className={`space-y-4 ${className}`}>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          {kicker && <div className="overline">{kicker}</div>}
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-emerald-950 tracking-tight">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Empty({ title = "Nothing here yet", note, action, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-dashed border-emerald-900/15 bg-white/60 p-10 text-center">
      <div className="font-display font-semibold text-lg text-emerald-950">{title}</div>
      {note && <div className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">{note}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Badge({ children, tone = "brand", testid }) {
  const tones = {
    brand: "bg-emerald-50 text-emerald-900 border-emerald-700/20",
    warm:  "bg-orange-50 text-orange-800 border-orange-700/20",
    slate: "bg-slate-100 text-slate-700 border-slate-300",
    indigo:"bg-indigo-50 text-indigo-800 border-indigo-700/20",
    rose:  "bg-rose-50 text-rose-800 border-rose-700/20",
  };
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Button({ variant = "primary", size = "md", children, className = "", ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none";
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const variants = {
    primary: "bg-[hsl(160,84%,17%)] text-white hover:bg-[hsl(160,84%,13%)]",
    secondary: "bg-white border border-emerald-900/15 text-emerald-900 hover:bg-emerald-50",
    ghost: "text-emerald-900 hover:bg-emerald-50",
    warm: "bg-orange-600 text-white hover:bg-orange-700",
    danger: "bg-rose-600 text-white hover:bg-rose-700",
  };
  return <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...rest}>{children}</button>;
}

export function Input({ label, testid, right, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-xs font-semibold text-emerald-900 tracking-wide uppercase">{label}</span>}
      <div className="relative mt-1">
        <input
          data-testid={testid}
          className={`w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)] focus:border-transparent ${className}`}
          {...rest}
        />
        {right && <div className="absolute inset-y-0 right-3 flex items-center">{right}</div>}
      </div>
    </label>
  );
}

export function Select({ label, testid, children, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-xs font-semibold text-emerald-900 tracking-wide uppercase">{label}</span>}
      <select
        data-testid={testid}
        className={`mt-1 w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)] ${className}`}
        {...rest}
      >
        {children}
      </select>
    </label>
  );
}

export function Textarea({ label, testid, className = "", ...rest }) {
  return (
    <label className="block">
      {label && <span className="text-xs font-semibold text-emerald-900 tracking-wide uppercase">{label}</span>}
      <textarea
        data-testid={testid}
        className={`mt-1 w-full rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(158,64%,40%)] ${className}`}
        {...rest}
      />
    </label>
  );
}
